import { DateTime } from '@ksyos/date-time';
import { Box, Button, ButtonRow, PageHeader } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useState } from 'react';
import { DatePickerFormGroupField } from '@frontend/components/fields/DatePickerField';
import { TextInputFormGroupField } from '@frontend/components/fields/TextInputField';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getSelectedLanguage } from '@frontend/translate';
import type { ValidationErrors } from '../../../../utils/validation';
import type { IPatientInOrganization } from './PatientSearch';
import type { IPatientSearchResult } from './PatientSearchStartBar';
import type { IPatientSearchInformation } from './searchPatientFunctions';
import { newPatientSearchBarSchema, newPatientSearchBarUpdateSchema } from '../../../../../common/validation/patient';
import { useAbortableFetch } from '../../../../utils/hooks/useAbortableFetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../../utils/validation';
import { Loader } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { ProcessStartWizardRow } from '../../../elements/ProcessStartWizard/ProcessStartWizardRow';
import { ProcessStartWizardCard } from '../../processStartWizard/components/ProcessStartWizardCard';
import { DuplicatePatientIdAlert } from '../DuplicatePatientIdAlert';
import { NoPatientResultBar, PatientSearchField } from './NoPatientResultBar';
import { PatientResultBar } from './PatientResultBar';
import { findPatientInKsyos, findPatientInSbvzAndVecozo, findPatientsInOrganization } from './searchPatientFunctions';
import { normalizePatient } from './utils';

export function NewPatientSearchBar() {
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const translate = getSelectedLanguage();

    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSearching, setIsSearching] = useState(false);
    const [duplicatePatientId, setDuplicatePatientId] = useState<string | undefined>();
    const [patients, setPatients] = useState<(IPatientSearchResult | IPatientInOrganization)[]>([]);
    const [searchState, setSearchState] = useState<IPatientSearchInformation>({
        birthDate: null,
        identifierSocialSecurity: null,
    });

    return (
        <PageTemplate>
            <PageHeader title={translate.patient.newPatient} />
            <ProcessStartWizardCard>
                <FormContextProvider
                    value={{
                        errors,
                        item: searchState,
                        onChange: handleChange,
                        onDoValidate: handleValidation,
                        placeholderGroupName: 'patient',
                    }}
                >
                    <ProcessStartWizardRow
                        header
                        label={translate.patient.newPatient}
                    >
                        <Box
                            display="grid"
                            flex="1"
                            gridTemplateColumns={['1fr', null, null, `minmax(0, 256px) min-content minmax(0, 1fr)`]}
                            sx={{ columnGap: 2 }}
                        >
                            <TextInputFormGroupField
                                dontValidateOnChange={true}
                                error={errors.get('identifierSocialSecurity')}
                                fieldKey="identifierSocialSecurity"
                                label=""
                                layout="vertical"
                                onEnterKeyPress={handleSearch}
                                placeholder={translate.common.bsn}
                            />
                            <DatePickerFormGroupField
                                color="secondary"
                                fieldKey="birthDate"
                                label=""
                                layout="vertical"
                                maxValue={DateTime.now().toDate()}
                            />
                            <ButtonRow
                                isStackedBelowSmall={true}
                                sx={{ alignItems: [null, 'flex-start'], justifyContent: 'space-between' }}
                            >
                                <Button
                                    className="automation-patientSearchBarButtonSearch"
                                    color="secondary"
                                    onClick={handleSearch}
                                    variant="outline"
                                >
                                    {translate.common.search}
                                </Button>
                                <Button
                                    onClick={handlePatientNoBsnClick}
                                    variant="link"
                                >
                                    {translate.patient.noBSN}
                                </Button>
                            </ButtonRow>
                        </Box>
                    </ProcessStartWizardRow>
                </FormContextProvider>
                {isSearching && <Loader fixedCentered />}
                {patients.length > 0 &&
                    patients.map((patient, index) => (
                        <PatientResultBar
                            disabled={isSearching && Boolean(duplicatePatientId)}
                            key={patient.id ?? `patient-${index}`}
                            onStart={() => handleStart(patient.id)}
                            patient={patient as IPatientSearchResult}
                        />
                    ))}
                {errors.has('noMatchDateBsn') && (
                    <NoPatientResultBar
                        errors={errors}
                        searchField={
                            searchState.birthDate
                                ? PatientSearchField.birthDate
                                : PatientSearchField.identifierSocialSecurity
                        }
                    />
                )}
                {duplicatePatientId && <DuplicatePatientIdAlert id={duplicatePatientId} />}
            </ProcessStartWizardCard>
        </PageTemplate>
    );

    function handlePatientNoBsnClick() {
        searchState.birthDate
            ? navigate(`/patients/noBsn?birthDate=${DateTime.from(searchState.birthDate).format('YYYY-MM-DD')}`)
            : navigate('/patients/noBsn');
    }

    function handleChange<K extends keyof IPatientSearchInformation>(name: K, value: IPatientSearchInformation[K]) {
        const cleanedValue =
            name === 'birthDate' && typeof value === 'string' ? DateTime.from(value).format('YYYY-MM-DD') : value;
        setSearchState((currentState) => ({ ...currentState, [name]: cleanedValue }));
    }

    function handleValidation(key: keyof IPatientSearchInformation) {
        if (key === 'birthDate') {
            const dateToValidate = searchState.birthDate
                ? DateTime.from(searchState.birthDate).format('YYYY-MM-DD')
                : null;
            setErrors(
                updateValidationErrorsForOne(
                    newPatientSearchBarSchema,
                    { ...searchState, birthDate: dateToValidate },
                    errors,
                    'birthDate'
                )
            );

            if (dateToValidate && DateTime.from(dateToValidate).isAfter(DateTime.now(), 'day')) {
                setErrors(errors.set('birthDate', 'date_future'));
            }
        }
        if (key !== 'birthDate') {
            validateOne(key);
        }
    }

    function validateOne(name: keyof IPatientSearchInformation) {
        setErrors(updateValidationErrorsForOne(newPatientSearchBarSchema, searchState, errors, name));
    }

    async function handleSearch() {
        setErrors(emptyValidationErrors);
        setPatients([]);

        const birthDate = searchState.birthDate && DateTime.from(searchState.birthDate).format('YYYY-MM-DD');

        const searchStateToValidate = {
            ...searchState,
            birthDate: birthDate,
            processNumber: null,
        };

        const { errors: validationErrors } = validate(newPatientSearchBarSchema, searchStateToValidate);
        setErrors(validationErrors);

        if (validationErrors.size > 0) {
            return;
        }

        if (searchState.birthDate && DateTime.from(searchState.birthDate).isAfter(DateTime.now(), 'day')) {
            setErrors(errors.set('birthDate', 'date_future'));

            return;
        }

        setIsSearching(true);

        const { patients: patientsInOrganization } = await findPatientsInOrganization(searchStateToValidate);

        if (patientsInOrganization.length > 0) {
            setPatients(patientsInOrganization);
        } else if (searchState.identifierSocialSecurity && searchState.birthDate) {
            const patientSearchResultInKsyos = await findPatientInKsyos(searchStateToValidate);
            if (patientSearchResultInKsyos && patientSearchResultInKsyos?.patient) {
                setPatients([patientSearchResultInKsyos?.patient]);
            } else {
                const patientSearchResult = await findPatientInSbvzAndVecozo({
                    birthDate: birthDate,
                    identifierSocialSecurity: searchState.identifierSocialSecurity,
                });
                if (patientSearchResult) {
                    setPatients([patientSearchResult.patient]);
                    await findDuplicatePatientId(patientSearchResult.patient);
                } else {
                    setErrors(emptyValidationErrors.set('noMatchDateBsn', 'noMatchDateBsn'));
                }
            }
        } else {
            setPatients([]);
        }

        setIsSearching(false);
    }

    async function findDuplicatePatientId(patient: IPatientSearchResult) {
        const normalizedPatient = normalizePatient(patient);
        const result = await abortableFetch.fetchJson('/api/patients/duplicates', 'POST', normalizedPatient);
        const patientId = result.json.count > 0 ? result.json.items[0].id : undefined;

        setDuplicatePatientId(patientId);
    }

    function handleStart(patientId?: string) {
        if (patientId) {
            return navigate(`/patients/${patientId}`);
        }

        handleCreatePatient();
    }

    async function handleCreatePatient() {
        if (patients?.length !== 1) {
            return;
        }

        const { errors: validationErrors, item: validatedPatient } = validate(
            newPatientSearchBarUpdateSchema,
            patients[0]
        );
        if (validationErrors.size === 0) {
            const newPatientData = { patient: { ...validatedPatient } };

            const result = await abortableFetch.fetchJson('/api/patients', 'POST', newPatientData);
            navigate(`/patients/${result.json.id}`);
        } else {
            const validationError = validationErrors.entries().next().value;
            throw new Error(`SBVZ/Vecozo patient data validation failed. ${validationError[0]}: ${validationError[1]}`);
        }
    }
}
